import {copyText} from '@github-ui/copy-to-clipboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CopilotIcon, CopyIcon, TerminalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Tooltip} from '@primer/react'
import {useCallback, useMemo} from 'react'

import styles from './OpenInLocalAgentButton.module.css'

type OpenInLocalAgentButtonProps = {
  url: string
}

function buildPrompt(issueUrl: string): string {
  return `Create a plan for the following GitHub issue: ${issueUrl}. Store the plan in .github/plans/issue-id.md`
}

function buildVSCodeAgentUrl(prompt: string): string {
  const doubleEncoded = encodeURIComponent(encodeURIComponent(prompt))
  return `vscode://github.copilot-chat?mode=agent&prompt=${doubleEncoded}`
}

export function OpenInLocalAgentButton({url}: OpenInLocalAgentButtonProps) {
  const enabled = isFeatureEnabled('issues_open_in_tool_links')
  if (!enabled) return null

  return (
    <div className={styles.container}>
      <OpenInLocalAgentButtonInner url={url} />
    </div>
  )
}

function OpenInLocalAgentButtonInner({url}: OpenInLocalAgentButtonProps) {
  const prompt = useMemo(() => buildPrompt(url), [url])
  const vsCodeUrl = useMemo(() => buildVSCodeAgentUrl(prompt), [prompt])

  const handleCopyAsPrompt = useCallback(() => {
    void copyText(prompt)
  }, [prompt])

  const handleOpenInVSCode = useCallback(() => {
    window.open(vsCodeUrl, '_self')
  }, [vsCodeUrl])

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Tooltip text="Open in Copilot CLI" type="description">
          <ActionMenu.Button aria-label="Open in Copilot CLI" leadingVisual={TerminalIcon} size="medium" />
        </Tooltip>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={handleCopyAsPrompt}>
            <ActionList.LeadingVisual>
              <CopyIcon />
            </ActionList.LeadingVisual>
            Copy as prompt
          </ActionList.Item>
          <ActionList.Item onSelect={handleOpenInVSCode}>
            <ActionList.LeadingVisual>
              <CopilotIcon />
            </ActionList.LeadingVisual>
            Copilot for VS Code
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
